<template>
  <div v-if="plan && !existingLeagueId" style="margin-top: 10px">
    <Fieldset :legend="plan.displayName + ' anlegen'">
      <div style="margin: 8px 0px">
        <span style="margin-right: 10px">Maximale Freilose in der 1. Runde</span>
        <InputNumber
          v-model="maxByes"
          :min="0"
          :max="999"
          showButtons
          buttonLayout="horizontal"
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
          inputStyle="width: 60px; text-align: center"
          @update:modelValue="loadPlan"
        />
      </div>
      <Message
        :severity="plan.possible ? 'info' : 'warn'"
        :closable="false"
        style="margin: 5px 0px"
        >{{ describePlan(plan) }}</Message
      >
      <Button
        label="Pokal anlegen und auslosen"
        icon="pi pi-sitemap"
        class="p-button-rounded mb2"
        :disabled="!plan.possible"
        @click="openCreationDialog"
      />
    </Fieldset>
  </div>
  <Dialog
    v-model:visible="displayCreationDialog"
    :closable="false"
    :style="{ width: '90vw', maxWidth: '1100px' }"
  >
    <template #header>
      <h3 v-if="plan">{{ plan.displayName }} anlegen und auslosen</h3>
    </template>
    <div v-if="plan">
      <Message severity="info" :closable="false">{{ describePlan(plan) }}</Message>
      <DataTable class="p-datatable-sm" :value="editableRounds" responsiveLayout="scroll">
        <Column header="Runde">
          <template #body="slotProps">
            {{ slotProps.data.name }}
          </template>
        </Column>
        <Column header="Partien">
          <template #body="slotProps">
            {{ slotProps.data.games
            }}<span v-if="slotProps.data.byes > 0">
              (davon {{ slotProps.data.byes }} Freilose)</span
            >
          </template>
        </Column>
        <Column header="Spieltagbeginn">
          <template #body="slotProps">
            <Calendar
              v-model="slotProps.data.gameDayStart"
              dateFormat="dd.mm.yy"
              :manualInput="true"
              inputStyle="width: 110px"
            />
          </template>
        </Column>
        <Column header="Spieltagende">
          <template #body="slotProps">
            <Calendar
              v-model="slotProps.data.gameDayEnd"
              dateFormat="dd.mm.yy"
              :manualInput="true"
              inputStyle="width: 110px"
            />
          </template>
        </Column>
        <Column header="Erwarteter Spieltag">
          <template #body="slotProps">
            <Calendar
              v-model="slotProps.data.expectedGameDate"
              dateFormat="dd.mm.yy"
              :showTime="true"
              hourFormat="24"
              :manualInput="true"
              inputStyle="width: 145px"
            />
          </template>
        </Column>
        <Column header="Abgabetermin Spielbericht">
          <template #body="slotProps">
            <Calendar
              v-model="slotProps.data.gameReportDeadline"
              dateFormat="dd.mm.yy"
              :showTime="true"
              hourFormat="24"
              :manualInput="true"
              inputStyle="width: 145px"
            />
          </template>
        </Column>
      </DataTable>
      <Message v-if="creationError" severity="error" :closable="false">{{
        creationError
      }}</Message>
      <ProgressSpinner v-if="creationInProgress" style="height: 40px" />
    </div>
    <template #footer>
      <Button
        label="Anlegen und auslosen"
        icon="pi pi-check"
        class="p-button-text"
        :disabled="creationInProgress"
        @click="createCup"
      />
      <Button
        label="Abbrechen"
        icon="pi pi-times"
        :disabled="creationInProgress"
        @click="displayCreationDialog = false"
      />
    </template>
  </Dialog>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import FailureHandler from "../../services/ServerConnection/FailureHandler";
import Fieldset from "primevue/fieldset";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Calendar from "primevue/calendar";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";

export default {
  name: "CupCreationView",
  components: {
    Fieldset,
    InputNumber,
    Button,
    Dialog,
    DataTable,
    Column,
    Calendar,
    Message,
    ProgressSpinner,
  },
  emits: ["created"],
  props: {
    scope: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      plan: null,
      existingLeagueId: null,
      defaultRounds: [],
      maxByes: 4,
      displayCreationDialog: false,
      editableRounds: [],
      creationInProgress: false,
      creationError: null,
    };
  },
  created() {
    this.loadPlan();
  },
  watch: {
    scope() {
      this.plan = null;
      this.loadPlan();
    },
  },
  methods: {
    loadPlan() {
      ServerConnection.get("cup/plan?scope=" + this.scope + "&maxByes=" + (this.maxByes || 0))
        .then((response) => response.data)
        .then((data) => {
          this.plan = data.plan;
          this.existingLeagueId = data.existingLeagueId;
          this.defaultRounds = data.rounds;
        })
        .catch((error) => {
          FailureHandler.handleServerFailure(this.$router, error);
        });
    },
    describePlan(plan) {
      if (plan.problem) {
        return plan.problem;
      }
      var firstRound = plan.rounds.filter((round) => round.numberOfGameDay === 1)[0];
      var spieltage =
        plan.rounds.length + (plan.rounds.length === 1 ? " Spieltag" : " Spieltage");
      if (plan.preliminaryRound) {
        return (
          plan.teamCount +
          " Mannschaften: Vorrunde mit " +
          plan.preliminaryGames +
          " Partien, deren Sieger direkt in die 1. Runde mit " +
          firstRound.games +
          " Partien gelost werden (" +
          spieltage +
          ")."
        );
      }
      return (
        plan.teamCount +
        " Mannschaften: 1. Runde mit " +
        firstRound.games +
        " Partien, davon " +
        plan.byes +
        " Freilose (" +
        spieltage +
        ")."
      );
    },
    openCreationDialog() {
      this.creationError = null;
      this.editableRounds = this.defaultRounds.map((round) => ({
        numberOfGameDay: round.numberOfGameDay,
        name: round.name,
        games: round.games,
        byes: round.byes,
        gameDayStart: this.parseDate(round.gameDayStart),
        gameDayEnd: this.parseDate(round.gameDayEnd),
        expectedGameDate: this.parseDate(round.expectedGameDate),
        gameReportDeadline: this.parseDate(round.gameReportDeadline),
      }));
      this.displayCreationDialog = true;
    },
    createCup() {
      this.creationInProgress = true;
      this.creationError = null;
      var body = {
        scope: this.scope,
        maxByes: this.maxByes || 0,
        rounds: this.editableRounds.map((round) => ({
          numberOfGameDay: round.numberOfGameDay,
          gameDayStart: this.formatDate(round.gameDayStart),
          gameDayEnd: this.formatDate(round.gameDayEnd),
          expectedGameDate: this.formatDateTime(round.expectedGameDate),
          gameReportDeadline: this.formatDateTime(round.gameReportDeadline),
        })),
      };
      ServerConnection.post("cup/create", JSON.stringify(body))
        .then(() => {
          this.creationInProgress = false;
          this.displayCreationDialog = false;
          this.loadPlan();
          this.$emit("created");
        })
        .catch((error) => {
          this.creationInProgress = false;
          if (error.response && error.response.status === 400) {
            this.creationError = error.response.data;
          } else {
            FailureHandler.handleServerFailure(this.$router, error);
          }
        });
    },
    parseDate(value) {
      if (!value) {
        return null;
      }
      return new Date(value.replace(" ", "T"));
    },
    formatDate(date) {
      if (!date) {
        return null;
      }
      return (
        date.getFullYear() +
        "-" +
        this.pad(date.getMonth() + 1) +
        "-" +
        this.pad(date.getDate())
      );
    },
    formatDateTime(date) {
      if (!date) {
        return null;
      }
      return (
        this.formatDate(date) +
        " " +
        this.pad(date.getHours()) +
        ":" +
        this.pad(date.getMinutes())
      );
    },
    pad(value) {
      return value < 10 ? "0" + value : "" + value;
    },
  },
};
</script>
